import { apiService } from './apiService';
import { ChatMessage, ProposalResolution } from '../models/clinicAgent';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

// Raw row shape from GET /api/chat/ (ChatSerializer).
interface ChatHistoryRow {
  id: number;
  message: string;
  role: 'user' | 'assistant';
  createdAt: string; // "dd-mm-yyyy HH:MM:SS"
  read: boolean;
  // Image attached to the message: a uri-compatible string (hosted URL for
  // history, or a base64 data URL). Fed straight to <img src>, no decoding.
  fileData?: string;
  fileType?: string;
  // Backend enhancement (not yet shipped): proposals joined from AgentRun.
  proposals?: unknown[];
  // "text" (default) or "proposal_resolution". Read both casings: existing
  // fields arrive camelCase, but the serializer may expose snake_case.
  messageType?: 'text' | 'proposal_resolution';
  message_type?: 'text' | 'proposal_resolution';
  // For a resolution: { run_id, proposal_id, action, status, patient_id, detail }.
  metadata?: Record<string, unknown>;
}

// Parse the backend's "dd-mm-yyyy HH:MM:SS" timestamp into epoch ms.
function parseTs(raw: string): number {
  const m = /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/.exec(raw || '');
  if (!m) return Date.now();
  const [, dd, mm, yyyy, hh, min, ss] = m;
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(ss)).getTime();
}

export const clinicChatHistoryService = {
  // Load the clinician's own chat transcript. GET only — never POST /api/chat/
  // from the clinic client (that runs the patient coach persona). Rows arrive
  // newest-first; we return them chronological for rendering.
  getHistory: async (page: number = 1): Promise<ChatMessage[]> => {
    const data = await apiService.get(`${API_BASE_URL}/chat/?page=${page}`);
    const rows: ChatHistoryRow[] = data?.results ?? data ?? [];
    const messages: ChatMessage[] = rows.map((r) => {
      const messageType = r.messageType ?? r.message_type ?? 'text';
      const base: ChatMessage = {
        id: `hist-${r.id}`,
        role: r.role,
        text: r.message ?? '',
        ts: parseTs(r.createdAt),
        ...(r.fileData ? { imageUrl: r.fileData } : {}),
      };

      // A resolved-proposal event card; metadata carries the resolution record.
      if (messageType === 'proposal_resolution' && r.metadata) {
        return {
          ...base,
          messageType: 'proposal_resolution',
          resolution: r.metadata as unknown as ProposalResolution,
        };
      }

      // Proposals on historical rows (if the backend adds them) are rendered
      // read-only as already-resolved; absent today, so left undefined.
      return {
        ...base,
        proposals: Array.isArray(r.proposals) && r.proposals.length > 0
          ? r.proposals.map((p: any) => ({ ...p, status: 'applied' as const }))
          : undefined,
      };
    });
    // API is newest-first; reverse to chronological.
    return messages.reverse();
  },
};
